import { StarIcon } from "@heroicons/react/24/outline";
import { Separator } from "@radix-ui/react-separator";
import clsx from "clsx";
import React from "react";
import { FormattedMessage } from "react-intl";
import Skeleton from "react-loading-skeleton";
import { Link } from "react-router-dom";

import { buildLocale } from "~/common/locale";
import { remoteLog } from "~/common/logging";
import { FormatDate } from "~/components/common/FormatDate";
import { PersonName } from "~/components/common/PersonName";
import { Button } from "~/components/ui/button";
import type { Loading } from "~/types/common";

import { ExpertImage, ExpertName, MeetingsTag } from "./meetings-shared";
import type { TExpert } from "./meetings-types";

export function ExpertCard({
  expert,
  onAboutClick,
}: {
  expert: Loading<TExpert>;
  onAboutClick?: (expert: TExpert) => void;
}) {
  return (
    <li>
      <ExpertCardDesktop expert={expert} onAboutClick={onAboutClick} />
      <ExpertCardMobile expert={expert} onAboutClick={onAboutClick} />
    </li>
  );
}

function ExpertCardDesktop({
  expert,
  onAboutClick,
}: {
  expert: Loading<TExpert>;
  onAboutClick?: (expert: TExpert) => void;
}) {
  const nearestSlotDateStr = expert ? getNearestSlotDateStr(expert) : undefined;
  const isNotAvailable = expert ? !nearestSlotDateStr : false;

  return (
    <article
      className={clsx(
        "tw-list-none tw-rounded-3xl tw-bg-white tw-p-5 tw-shadow-sm max-sm:tw-hidden",
        {
          "tw-opacity-70 tw-transition-opacity hover:tw-opacity-100": isNotAvailable,
        },
      )}
    >
      {/* Mobile */}
      <div className="tw-flex tw-items-center tw-gap-8 sm:tw-hidden">
        <ExpertImage expert={expert} />

        <div className="tw-flex tw-flex-col tw-gap-4">
          <ExpertInfo expert={expert} />
          <ExpertActions expert={expert} onAboutClick={onAboutClick} />
        </div>
      </div>

      {/* Desktop */}
      <div className="tw-flex tw-items-center tw-justify-between tw-gap-4 max-sm:tw-hidden">
        <div className="tw-flex tw-min-w-0 tw-flex-1 tw-items-center tw-gap-4">
          <ExpertImage expert={expert} />
          <ExpertInfo expert={expert} />
        </div>

        <ExpertActions expert={expert} onAboutClick={onAboutClick} className="tw-shrink-0" />
      </div>
    </article>
  );
}

export function ExpertInfo({
  expert,
  displayNearestSlot = true,
  displayDescription = true,
  averageRatingStyle = "stars",
}: {
  expert: Loading<TExpert>;
  displayNearestSlot?: boolean;
  displayDescription?: boolean;
  averageRatingStyle?: "stars" | "number";
}) {
  const averageRatingAsInt = expert ? Math.ceil(expert.average_rating ?? 0) : 0;
  const tag = expert ? getExpertTag(expert) : undefined;
  const nearestSlotDateStr = expert ? getNearestSlotDateStr(expert) : undefined;

  return expert ? (
    <div className="tw-flex tw-min-w-0 tw-flex-col tw-gap-1">
      <div className="tw-flex tw-flex-wrap tw-items-start tw-gap-2">
        <PersonName
          person={expert}
          useMiddleName
          className="tw-shrink-0 tw-text-base tw-font-semibold tw-text-brand-black"
        />

        {displayDescription && tag ? (
          <MeetingsTag className={clsx("tw-min-w-0 tw-shrink-0 tw-text-xs", tag.className)}>
            {tag.text}
          </MeetingsTag>
        ) : null}
      </div>

      <div className="tw-flex tw-items-center tw-gap-2">
        {averageRatingStyle == "stars" ? <ExpertRating rating={averageRatingAsInt} /> : null}

        {averageRatingStyle == "number" ? (
          <div className="tw-flex tw-items-center tw-gap-1">
            <StarIcon className="tw-h-5 tw-w-5" />
            {expert.average_rating?.toFixed(1)}
          </div>
        ) : null}
      </div>

      {displayNearestSlot ? (
        <div className="tw-text-brand-gray sm:tw-text-[15px]">
          <FormattedMessage id="meetings.nearest_slot" />
          : <br className="sm:tw-hidden" />
          {nearestSlotDateStr ? (
            <FormatDate value={nearestSlotDateStr} date time />
          ) : (
            <FormattedMessage id="meetings.nearest_slot.empty" />
          )}
        </div>
      ) : null}
    </div>
  ) : (
    <Skeleton
      className="tw-rounded-md"
      containerClassName="tw-space-y-2"
      count={3}
      width={250}
      height={15}
    />
  );
}

export function hasExpertProfile(expert: TExpert): boolean {
  const hasVideo = Boolean(expert.video_url && String(expert.video_url).trim());
  const hasDescription = Boolean(expert.description && String(expert.description).trim());
  const hasSpecializations =
    Array.isArray(expert.specializations) && expert.specializations.length > 0;
  return hasVideo || hasDescription || hasSpecializations;
}

function ExpertActions({
  expert,
  onAboutClick,
  className,
}: {
  expert: Loading<TExpert>;
  onAboutClick?: (expert: TExpert) => void;
  className?: string;
}) {
  const actionsClassName = clsx(
    "tw-flex tw-items-center tw-gap-3 tw-rounded-xl max-sm:tw-w-full max-sm:tw-flex-col max-sm:tw-items-stretch",
    className,
  );

  if (!expert) {
    return (
      <div className={actionsClassName}>
        <Skeleton width={140} height={50} className="tw-rounded-xl" />
        <Skeleton width={150} height={50} className="tw-rounded-xl" />
      </div>
    );
  }

  const canOpenAbout = hasExpertProfile(expert);

  return (
    <div className={actionsClassName}>
      <button
        type="button"
        disabled={!canOpenAbout}
        onClick={() => canOpenAbout && onAboutClick?.(expert)}
        className={clsx(
          "tw-rounded-xl tw-border-0 tw-px-7 tw-py-3 tw-text-base tw-font-semibold",
          "tw-inline-flex tw-shrink-0 tw-items-center tw-justify-center tw-whitespace-nowrap tw-transition-colors",
          "max-sm:tw-w-full max-sm:tw-whitespace-normal",
          "tw-bg-[#f2f5f8] tw-text-[#7dafda]",
          "hover:tw-bg-[#7dafda] hover:tw-text-white",
          "disabled:tw-cursor-not-allowed disabled:tw-opacity-65",
          "disabled:hover:tw-bg-[#f2f5f8] disabled:hover:tw-text-[#7dafda]",
        )}
      >
        <FormattedMessage id="meetings.about_expert" />
      </button>
      <ExpertLink expert={expert} className="max-sm:tw-w-full" />
    </div>
  );
}

function ExpertLink({ expert, className }: { expert: Loading<TExpert>; className?: string }) {
  className = clsx("tw-rounded-xl max-sm:tw-self-start", className);

  if (!expert) {
    return (
      <Skeleton
        containerClassName="max-sm:tw-w-full"
        className={className}
        width={150}
        height={50}
      />
    );
  }

  const nearestSlotDateStr = getNearestSlotDateStr(expert);
  if (!nearestSlotDateStr) {
    // Expert has no nearest slot available and, because link can't be disabled,
    // we display a disabled button instead.

    return (
      <Button rounded="extra" size="2xl" className={className} disabled>
        <FormattedMessage id="meetings.book" />
      </Button>
    );
  }

  return (
    <Button variant="primary" rounded="extra" size="2xl" className={className} asChild>
      <Link
        to={`/pages/meetings/book/${expert.account_id}`}
        data-testid="meetings-expert-book-link"
      >
        <FormattedMessage id="meetings.book" />
      </Link>
    </Button>
  );
}

function ExpertRating({ rating }: { rating: number }) {
  return (
    <div className="tw-max-h-[22px]">
      {Array.from({ length: rating }).map((_, i) => (
        <CustomStarIcon key={i} />
      ))}
    </div>
  );
}

function ExpertCardMobile({
  expert,
  onAboutClick,
}: {
  expert: Loading<TExpert>;
  onAboutClick?: (expert: TExpert) => void;
}) {
  const averageRatingAsInt = expert ? Math.ceil(expert.average_rating ?? 0) : 0;
  const nearestSlotDateStr = expert ? getNearestSlotDateStr(expert) : undefined;
  const isNotAvailable = expert ? !nearestSlotDateStr : false;
  const tag = expert ? getExpertTag(expert) : undefined;

  return (
    <article
      className={clsx(
        "tw-flex tw-max-w-[700px] tw-flex-col tw-gap-6 tw-rounded-3xl tw-bg-white tw-p-5 tw-shadow-sm sm:tw-hidden",
        { "tw-transition-opacity hover:tw-opacity-100 sm:tw-opacity-70": isNotAvailable },
      )}
    >
      <div className="tw-flex tw-flex-col tw-gap-1">
        <div className="tw-flex tw-justify-between tw-gap-3">
          <div className="tw-flex tw-flex-col tw-justify-center tw-gap-2">
            {expert ? (
              <span className="tw-inline-flex tw-items-center tw-gap-2 tw-text-[#808080]">
                <FormattedMessage id="meetings.expert" />

                {tag ? (
                  <MeetingsTag className={clsx("tw-text-xs", tag.className)}>
                    {tag.text}
                  </MeetingsTag>
                ) : null}
              </span>
            ) : (
              <Skeleton className="tw-rounded-md" width={150} height={20} />
            )}

            {expert ? (
              <ExpertName expert={expert} />
            ) : (
              <Skeleton className="tw-rounded-md" width={150} height={30} />
            )}
          </div>

          <ExpertImage expert={expert} />
        </div>

        {expert ? (
          <ExpertRating rating={averageRatingAsInt} />
        ) : (
          <Skeleton className="tw-rounded-md" width={110} height={20} />
        )}
      </div>

      <Separator className="tw-h-px tw-bg-[#D9DCE7]" />

      <div className="tw-flex tw-flex-col tw-items-center tw-justify-between tw-gap-4">
        <div className="tw-self-start tw-text-brand-gray">
          {expert ? (
            <>
              <FormattedMessage id="meetings.nearest_slot" />:
            </>
          ) : (
            <Skeleton className="tw-rounded-md" width={200} height={20} />
          )}

          {expert ? <br /> : null}

          {expert ? (
            nearestSlotDateStr ? (
              formatRelativeDateTime(nearestSlotDateStr)
            ) : (
              <FormattedMessage id="meetings.nearest_slot.empty" />
            )
          ) : (
            <Skeleton className="tw-mt-2 tw-rounded-md" width={200} height={20} />
          )}
        </div>

        <ExpertActions expert={expert} onAboutClick={onAboutClick} className="tw-w-full" />
      </div>
    </article>
  );
}

function formatRelativeDateTime(dateStr: string): string {
  const date = new Date(dateStr);
  const todayDate = new Date();

  const formatter = new Intl.DateTimeFormat(buildLocale, {
    day: "2-digit",
    month: "long",
    year: date.getFullYear() !== todayDate.getFullYear() ? "numeric" : undefined,
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  });

  try {
    // NOTE: For some doctors sometimes throws `RangeError: date value is not finite in DateTimeFormat format()`.
    return formatter.format(date);
  } catch (err) {
    remoteLog({ err, dateStr }, "formatRelativeDateTime()");
    return "";
  }
}

export type ExpertTag = {
  text: string;
  className?: string;
};

// Shared account-id to tag mapping used by legacy and redesign cards.
export function getExpertTag(expert: TExpert): ExpertTag | undefined {
  const MOLOTOVA_ACCOUNT_ID = 3320;
  const SOLOP_ACCOUNT_ID = 3342;

  switch (expert.account_id) {
    case MOLOTOVA_ACCOUNT_ID:
      return { text: "Дети" };

    case SOLOP_ACCOUNT_ID:
      return {
        text: "Консультирует только по пациентам с ДВНЧС",
        className: "tw-bg-[#C19B72]",
      };

    default:
      return undefined;
  }
}

export function getNearestSlotDateStr(expert: TExpert): string | undefined {
  if (!expert.nearest_slot?.times?.length) {
    return undefined;
  }

  const now = new Date();
  // Minimum time is 1 hour from now (for clinical expert bookings)
  const minBookingTime = new Date(now.getTime() + 60 * 60 * 1000);

  // Find the first available slot that is at least 1 hour from now
  const availableSlot = expert.nearest_slot.times.find(({ time }) => {
    const slotTime = new Date(time);
    return slotTime > minBookingTime;
  });

  return availableSlot?.time;
}

function CustomStarIcon() {
  return (
    <svg width="19" height="22" viewBox="0 0 21 22" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path
        d="M9.09814 3.27095C9.43614 2.43519 10.6194 2.43519 10.9574 3.27095L12.3983 6.83375C12.5419 7.18895 12.8754 7.43125 13.2576 7.4581L17.0913 7.72746C17.9906 7.79065 18.3562 8.91602 17.6658 9.49573L14.7226 11.967C14.4292 12.2134 14.3018 12.6054 14.3944 12.9772L15.3229 16.7065C15.5407 17.5813 14.5834 18.2768 13.8187 17.7994L10.5589 15.7639C10.2339 15.561 9.82166 15.561 9.49667 15.7639L6.23684 17.7994C5.47215 18.2768 4.51486 17.5813 4.73266 16.7065L5.66116 12.9772C5.75372 12.6054 5.62634 12.2134 5.33291 11.967L2.38974 9.49573C1.69933 8.91602 2.06498 7.79065 2.96428 7.72746L6.79795 7.4581C7.18015 7.43125 7.51365 7.18895 7.6573 6.83375L9.09814 3.27095Z"
        fill="#5BDCDC"
      />
    </svg>
  );
}
